// Barras horizontais de uma mesma medida (as três fases, as pontes, o carregamento em % do
// nominal), no formato de medidor: numa linha o nome à esquerda e o valor à direita, logo
// abaixo a barra na largura toda. Assim cabe no celular sem espremer a barra. Um eixo só,
// barras finas com ponta arredondada e uma linha tracejada no nominal (explicada no título
// do cartão, não em cima do gráfico).

import { useEffect, useMemo, useRef, useState } from "react";
import { seriesColor, token } from "../../lib/palette";
import { EChart, useThemeVersion } from "./EChart";

export interface BarItem {
  label: string;
  /** Valor já na unidade exibida (ou em % do nominal). */
  value: number | null;
  /** Texto do valor, à direita do nome. */
  text: string;
}

const ROW = 42;

export function Bars({
  items,
  max,
  mark,
  unit,
  stale,
  title,
}: {
  items: BarItem[];
  max: number;
  /** Linha de referência (o nominal), na mesma unidade dos valores. */
  mark?: number;
  unit: string;
  stale?: boolean;
  title: string;
}) {
  const theme = useThemeVersion();
  // A linha de nome + valor ocupa a largura do gráfico: precisa saber qual é.
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setWidth(Math.round(e!.contentRect.width)));
    ro.observe(box.current!);
    return () => ro.disconnect();
  }, []);

  const option = useMemo(() => {
    const muted = token("--muted");
    const fg = token("--fg");
    // Largura do gráfico menos as margens da grade e o recuo do rótulo interno.
    const plot = Math.max(0, width - 30);
    return {
      animationDurationUpdate: 600,
      grid: { left: 8, right: 8, top: 18, bottom: 22, containLabel: false },
      tooltip: {
        trigger: "item",
        backgroundColor: token("--surface"),
        borderColor: token("--line"),
        textStyle: { color: fg, fontSize: 12 },
        formatter: (p: { dataIndex: number }) => `${items[p.dataIndex]!.label}: <b>${items[p.dataIndex]!.text}</b>`,
      },
      xAxis: {
        type: "value",
        min: 0,
        max,
        splitNumber: 4,
        // 0–100 %: marcas de 25 em 25 (sem isso sai 0, 30, 60, 90 e um 100 encavalado).
        ...(unit === "%" && max === 100 ? { interval: 25 } : {}),
        axisLabel: {
          color: muted,
          fontSize: 10,
          hideOverlap: true,
          formatter: (v: number) => `${v.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}${unit === "%" ? "%" : ""}`,
        },
        splitLine: { lineStyle: { color: token("--line"), opacity: 0.6 } },
      },
      yAxis: {
        type: "category",
        inverse: true,
        data: items.map((i) => i.label),
        axisTick: { show: false },
        axisLine: { show: false },
        // Nome e valor na mesma linha, acima da barra.
        axisLabel: {
          inside: true,
          align: "left",
          verticalAlign: "bottom",
          padding: [0, 0, 9, 0],
          width: plot,
          overflow: "truncate",
          formatter: (_: string, i: number) => `{n|${items[i]!.label}}{v|${items[i]!.text}}`,
          rich: {
            n: { color: fg, fontSize: 12 },
            v: { color: muted, fontSize: 12, align: "right" },
          },
        },
        z: 10,
      },
      series: [
        {
          type: "bar",
          barWidth: 10,
          // Sem dado não é zero: a barra some, e o texto diz "—".
          data: items.map((i) => (i.value === null ? null : Math.min(max, Math.max(0, i.value)))),
          itemStyle: { color: stale ? muted : seriesColor(0), borderRadius: [0, 4, 4, 0] },
          showBackground: true,
          backgroundStyle: { color: token("--surface-2"), borderRadius: [0, 4, 4, 0] },
          markLine:
            mark === undefined
              ? undefined
              : {
                  symbol: "none",
                  silent: true,
                  lineStyle: { color: muted, type: "dashed", width: 1 },
                  label: { show: false },
                  data: [{ xAxis: mark }],
                },
        },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(items), max, mark, unit, stale, theme, width]);

  const label = `${title}: ${items.map((i) => `${i.label} ${i.text}`).join("; ")}`;
  return (
    <div ref={box}>
      <EChart option={option} height={items.length * ROW + 40} label={label} />
    </div>
  );
}
